import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { formatListingStatPeriodLine } from './listingStatPeriodCaption';

const LISTING_STAT_ROW_SX = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  columnGap: { xs: 1.25, sm: 1.5 },
  width: '100%',
};

const LISTING_STAT_ROWS_STACK_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 1.75, sm: 2.25 },
  width: '100%',
};

const ListingStatPill = ({ label, value, periodLine }) => (
  <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
    <Typography
      sx={{
        fontSize: '0.6rem',
        color: 'rgba(0,0,0,0.4)',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: '0.85rem',
        fontWeight: 600,
        color: 'rgba(0,0,0,0.72)',
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {value}
    </Typography>
    {periodLine && (
      <Typography
        sx={{
          mt: 0.15,
          fontSize: '0.6rem',
          color: 'rgba(0,0,0,0.4)',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {periodLine}
      </Typography>
    )}
  </Box>
);

const RollupStats = ({ lifetimeRollup, periodRollup, timeRangeLabel }) => {
  if (!lifetimeRollup) return null;
  const period = periodRollup || {
    inquiries: 0,
    opens: 0,
    sessions: 0,
    shuffles: 0,
    shares: 0,
    downloads: 0,
  };

  const periodLine = (value) => formatListingStatPeriodLine(value, timeRangeLabel);

  return (
    <Box sx={LISTING_STAT_ROWS_STACK_SX}>
      <Box sx={LISTING_STAT_ROW_SX}>
        <ListingStatPill
          label="Listing interest"
          value={lifetimeRollup.inquiries.toLocaleString()}
          periodLine={periodLine(period.inquiries)}
        />
        <ListingStatPill
          label="Views"
          value={lifetimeRollup.opens.toLocaleString()}
          periodLine={periodLine(period.opens)}
        />
        <ListingStatPill
          label="Sessions"
          value={lifetimeRollup.sessions.toLocaleString()}
          periodLine={periodLine(period.sessions)}
        />
      </Box>
      <Box sx={LISTING_STAT_ROW_SX}>
        <ListingStatPill
          label="Shuffles"
          value={lifetimeRollup.shuffles.toLocaleString()}
          periodLine={periodLine(period.shuffles)}
        />
        <ListingStatPill
          label="Shares"
          value={lifetimeRollup.shares.toLocaleString()}
          periodLine={periodLine(period.shares)}
        />
        <ListingStatPill
          label="Downloads"
          value={lifetimeRollup.downloads.toLocaleString()}
          periodLine={periodLine(period.downloads)}
        />
      </Box>
    </Box>
  );
};

/**
 * Roll-up strip for linked listing health — sits above the Floor plans /
 * Listings library so partners see property-level totals without scrolling
 * every row.
 */
const LinkedListingsSummaryBand = ({
  periodRollup,
  lifetimeRollup,
  linkedCount = 0,
  statusFilterLabel = null,
  timeRangeLabel = '',
  loading = false,
}) => {
  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        height={88}
        sx={{ borderRadius: '12px', bgcolor: 'rgba(0,0,0,0.04)', mb: { xs: 1.25, sm: 1.5 } }}
      />
    );
  }

  if (linkedCount <= 0 && !statusFilterLabel) return null;

  const countLabel = `${linkedCount} linked listing${linkedCount === 1 ? '' : 's'}`;
  const headerLabel = statusFilterLabel ? `${countLabel} · ${statusFilterLabel}` : countLabel;

  return (
    <Box
      sx={{
        mb: { xs: 1.25, sm: 1.5 },
        px: { xs: 1.5, sm: 1.75 },
        py: { xs: 1.25, sm: 1.5 },
        borderRadius: '12px',
        border: '1px solid rgba(99,102,241,0.12)',
        bgcolor: 'rgba(99,102,241,0.03)',
      }}
    >
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: 'rgba(0,0,0,0.62)', mb: 1.25 }}>
        {headerLabel}
      </Typography>

      <RollupStats
        lifetimeRollup={lifetimeRollup}
        periodRollup={periodRollup}
        timeRangeLabel={timeRangeLabel}
      />
    </Box>
  );
};

export default LinkedListingsSummaryBand;
